import { z } from 'zod'

/**
 * SPEC.md §4.2 / §11.1 — version markers and the default block lists.
 *
 * The lists here seed a brand new server's `ServerConfig`. They are deliberately
 * conservative: everything on them is either impossible to obtain legitimately
 * on a roleplay server or is already restricted by most frameworks. Server
 * owners edit their own copy in the panel; changing a default here only affects
 * servers created afterwards.
 */

export const SHARED_VERSION = '0.1.0'

/** Bumped when the agent wire format changes incompatibly (AGENT-PROTOCOL.md). */
export const PROTOCOL_VERSION = 1

/** Agents below this version are refused with `426 AGENT_VERSION_UNSUPPORTED`. */
export const MIN_AGENT_VERSION = '1.0.0'

/** The `ServerConfig.schemaVersion` this build reads and writes. */
export const CONFIG_SCHEMA_VERSION = 1

export const KEY_PREFIXES = {
  license: 'sacl',
  apiKey: 'sacp',
  upload: 'sacu',
} as const

/**
 * Vocabulary shared by the config, the agent protocol and the panel API.
 *
 * These live here rather than in `agent.ts` or `panel.ts` so both wire contracts
 * describe the same values: a framework name or a command type must mean exactly
 * one thing across the database, the Lua agent and the browser.
 */

/** Frameworks the resource has explicit integration hooks for (SPEC.md §8.14). */
export const FRAMEWORKS = ['standalone', 'esx', 'qbcore', 'qbox', 'vrp'] as const
export const FrameworkSchema = z.enum(FRAMEWORKS)
export type Framework = (typeof FRAMEWORKS)[number]

/** How a license binds to a game server's IP (SPEC.md §10.1). */
export const BIND_MODES = ['first_use', 'manual', 'none'] as const
export const BindModeSchema = z.enum(BIND_MODES)
export type BindMode = (typeof BIND_MODES)[number]

/** A world position, as the server reads it with `GetEntityCoords`. */
export const CoordsSchema = z.object({
  x: z.number().min(-1e7).max(1e7),
  y: z.number().min(-1e7).max(1e7),
  z: z.number().min(-1e7).max(1e7),
})
export type Coords = z.infer<typeof CoordsSchema>

/**
 * Weapons no legitimate roleplay loadout hands out. Explosive and ray weapons
 * dominate the list because they are what spawned-in cheat loadouts reach for.
 */
export const DEFAULT_BLACKLISTED_WEAPONS = [
  'WEAPON_RAILGUN',
  'WEAPON_RAYPISTOL',
  'WEAPON_RAYCARBINE',
  'WEAPON_RAYMINIGUN',
  'WEAPON_MINIGUN',
  'WEAPON_RPG',
  'WEAPON_HOMINGLAUNCHER',
  'WEAPON_GRENADELAUNCHER',
  'WEAPON_COMPACTLAUNCHER',
  'WEAPON_FIREWORK',
  'WEAPON_STICKYBOMB',
  'WEAPON_PROXMINE',
  'WEAPON_PIPEBOMB',
  'WEAPON_EMPLAUNCHER',
] as const

/** Ped models that are either non-human or classic troll models. */
export const DEFAULT_BLACKLISTED_PEDS = [
  's_m_m_movalien_01',
  'u_m_y_rsranger_01',
  'ig_orleans',
  'u_m_y_zombie_01',
  'a_c_chimp',
  'a_c_rhesus',
] as const

/** Military and weaponised vehicles. */
export const DEFAULT_BLACKLISTED_VEHICLES = [
  'rhino',
  'lazer',
  'hydra',
  'savage',
  'cargoplane',
  'blimp',
  'blimp2',
  'blimp3',
  'valkyrie',
  'hunter',
  'akula',
  'oppressor',
  'oppressor2',
  'khanjali',
  'chernobog',
  'thruster',
  'avenger',
  'deluxo',
  'stromberg',
  'jet',
  'titan',
  'bombushka',
  'volatol',
  'strikeforce',
  'nokota',
  'pyro',
  'molotok',
  'rogue',
  'starling',
  'tula',
] as const

/** Objects used for prop-spam and crash attacks. */
export const DEFAULT_BLACKLISTED_OBJECTS = [
  'prop_gascyl_01a',
  'prop_barrel_exp_01a',
  'prop_fnclink_05crnr1',
  'prop_rock_4_big2',
  'p_spinning_anus_s',
  'prop_windmill_01',
  'prop_mil_crate_01',
  'prop_container_01a',
  'prop_dock_crane_02',
] as const

/**
 * `eExplosionTag` values a player should never originate: tank shell, hi-octane,
 * aircraft and the directed-flame variants used by explosion spammers.
 */
export const DEFAULT_BLACKLISTED_EXPLOSIONS = [4, 5, 7, 9, 10, 12, 20, 26, 29, 30, 31, 32, 33, 34, 35, 36] as const

/** Particle dictionaries used by menu visual effects. */
export const DEFAULT_BLACKLISTED_PTFX = [
  'scr_rcbarry1',
  'scr_rcbarry2',
  'scr_agencyheistb',
  'scr_ornate_heist',
  'scr_xs_celebration',
  'scr_bike_adversary',
  'scr_ie_svm_technical',
] as const

/** Event names that only exist because a cheat menu triggers them. */
export const DEFAULT_BLACKLISTED_EVENTS = [
  'HCheat:TempDisableDetection',
  'adminmenu:allowall',
  'esx_society:withdrawMoneyX',
  'Dopamine:ExecuteCommand',
  'SkidMenu:Bypass',
  'Lumia:Bypass',
  'Absolute:Detect',
  'Kazo:AC',
  'Macho:Disable',
] as const

/** Substrings that betray a known menu in chat or in an event payload. */
export const DEFAULT_BLACKLISTED_STRINGS = [
  'eulen',
  'redengine',
  'hxsoftware',
  'skript.gg',
  'hydro menu',
  'susano',
  'lynx menu',
  'dopamine',
  'absolute menu',
  'desudo',
] as const

/**
 * Decoy server events. A legitimate resource never triggers these, so anything
 * that does is running injected code. NEVER sent to clients (SPEC.md §8.9).
 */
export const DEFAULT_HONEYPOT_SERVER_EVENTS = [
  'sentinel_ac:sv:disableChecks',
  'bank:withdrawAll',
  'admin:giveAllWeapons',
  'esx:giveMoneyX',
  'qb:addItemX',
  'vehicle:spawnAny',
] as const

export const DEFAULT_HONEYPOT_CLIENT_EVENTS = ['sentinel_ac:cl:disable', 'sentinel_ac:cl:setGod'] as const
export const DEFAULT_HONEYPOT_CLIENT_EXPORTS = ['DisableAntiCheat', 'SetBypassAll'] as const
export const DEFAULT_HONEYPOT_CLIENT_COMMANDS = ['sacbypass', 'acdisable'] as const

/**
 * Server-side decoys. These names MUST stay disjoint from the client sets above:
 * the client config is broadcast to every player, so a name shared between the
 * two would hand a cheater the server-side trap for free (SPEC.md §8.9). The
 * `client-config-subset` test enforces the disjointness.
 */
export const DEFAULT_HONEYPOT_SERVER_EXPORTS = ['SetPlayerImmune', 'ClearDetectionState'] as const
export const DEFAULT_HONEYPOT_SERVER_COMMANDS = ['sacoff'] as const

/**
 * Texture dictionaries drawn by known menus. Names rotate as menus update, so
 * this list is cloud-updatable and is only ever evidence, never a lone verdict.
 */
export const DEFAULT_MENU_TEXTURES: ReadonlyArray<{ dict: string; name: string }> = [
  { dict: 'HydroMenu', name: 'HydroMenuHeader' },
  { dict: 'Lumia', name: 'Header' },
  { dict: 'Dopamine', name: 'Header' },
  { dict: 'Fallout', name: 'Header' },
  { dict: 'Kazo', name: 'Banner' },
  { dict: 'Macho', name: 'Header' },
  { dict: 'Susano', name: 'Logo' },
  { dict: 'Eulen', name: 'Header' },
  { dict: 'Absolute', name: 'Header' },
  { dict: 'Skid', name: 'Header' },
  { dict: 'redengine', name: 'logo' },
  { dict: 'Deluxmeme', name: 'Header' },
]

/** Resources that always exist and must never count as an injection (SPEC.md §8.2). */
export const INTERNAL_RESOURCES = [
  '_cfx_internal',
  'monitor',
  'webadmin',
  'sessionmanager',
  'hardcap',
  'mapmanager',
  'spawnmanager',
  'chat',
  'baseevents',
  'screenshot-basic',
  'sentinel_ac',
] as const

/** Lua event names the resource uses. Frozen by SPEC.md §12; the panel shows them in the docs page. */
export const LUA_EVENTS = {
  clientInit: 'sentinel_ac:cl:init',
  clientConfig: 'sentinel_ac:cl:config',
  clientHeartbeat: 'sentinel_ac:cl:hb',
  clientScreenshot: 'sentinel_ac:cl:screenshot',
  serverHello: 'sentinel_ac:sv:hello',
  serverReady: 'sentinel_ac:sv:ready',
  serverReport: 'sentinel_ac:sv:report',
  serverHeartbeatAck: 'sentinel_ac:sv:hbAck',
  serverHoneypot: 'sentinel_ac:sv:honeypot',
  serverNewResource: 'sentinel_ac:sv:newResource',
  serverResourceList: 'sentinel_ac:sv:resourceList',
  serverIntegrity: 'sentinel_ac:sv:integrity',
  serverStopping: 'sentinel_ac:sv:stopping',
  serverScriptCam: 'sentinel_ac:sv:scriptCam',
  /** Client tells the server a jump started, so it can burst-sample `IsPlayerUsingSuperJump`. */
  serverJumpStart: 'sentinel_ac:sv:jumpStart',
  /** From `shared/probe.lua`, living inside another resource so it survives our own death. */
  serverProbeHeartbeatAck: 'sentinel_ac:sv:probeHbAck',
  serverProbeStop: 'sentinel_ac:sv:probeStop',
  /** From `include/client_guard.lua`, running inside a protected resource. */
  serverGuardReport: 'sentinel_ac:sv:guardReport',
  /** A guarded resource attributing a state change it is about to cause. */
  serverGuardClaim: 'sentinel_ac:sv:guardClaim',
  /** A guarded resource attributing the entities it just created. */
  serverGuardSpawn: 'sentinel_ac:sv:guardSpawn',

  /** In-game admin menu. Every request is re-authorised on the server. */
  serverMenuOpen: 'sentinel_ac:sv:menuOpen',
  serverMenuRefresh: 'sentinel_ac:sv:menuRefresh',
  serverMenuAction: 'sentinel_ac:sv:menuAction',
  clientMenuToggle: 'sentinel_ac:cl:menuToggle',
  clientMenuData: 'sentinel_ac:cl:menuData',
  clientMenuDenied: 'sentinel_ac:cl:menuDenied',
  clientMenuResult: 'sentinel_ac:cl:menuResult',
  serverScreenshotDone: 'sentinel_ac:sv:screenshotDone',
  /** Emitted for other resources to consume (SPEC.md §8.11). */
  outPlayerBanned: 'sentinel_ac:playerBanned',
  outPlayerKicked: 'sentinel_ac:playerKicked',
  outPlayerUnbanned: 'sentinel_ac:playerUnbanned',
  outDetection: 'sentinel_ac:detection',
  outConfigApplied: 'sentinel_ac:configApplied',
  outConnectChecked: 'sentinel_ac:connectChecked',
} as const

/** State-bag keys written by the resource server side (SPEC.md §8.11). */
export const STATE_BAG_KEYS = {
  bypass: 'sac:bypass',
  ready: 'sac:ready',
  configVersion: 'sac:cfgv',
  graceUntil: 'sac:graceUntil',
  allowedWeapons: 'sac:allowedWeapons',
  allowedModels: 'sac:allowedModels',
  teleportUntil: 'sac:tpUntil',
  spectate: 'sac:spectate',
  scriptCam: 'sac:scriptCam',
  nightVision: 'sac:nv',
} as const

/** Command types the cloud can queue for an agent (SPEC.md §3.1 `command_type`). */
export const COMMAND_TYPES = ['kick', 'ban', 'unban', 'screenshot', 'message', 'reloadConfig'] as const
export const CommandTypeSchema = z.enum(COMMAND_TYPES)
export type CommandType = (typeof COMMAND_TYPES)[number]
